import React from 'react';
import { supabase } from './supabase.js';
import { fetchAdminSeguimientoBadgeCounts } from './adminSeguimientoLecturas.js';

/** Estilo del punto rojo de aviso (menú admin, top bar). */
export const ADMIN_ALERT_DOT_STYLE = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: 'rgba(226,75,74,0.95)',
  boxShadow: '0 0 0 2px rgba(10,10,15,0.92)',
  flexShrink: 0,
  display: 'inline-block',
};

/**
 * Cuenta pendientes por sección del panel admin (ítems sin marcar como leídos).
 * @param {string} adminUserId
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchAdminMenuBadgeCounts(adminUserId) {
  if (!adminUserId) {
    return emptyAdminMenuBadges();
  }

  const seg = await fetchAdminSeguimientoBadgeCounts(adminUserId);
  if (!seg) return emptyAdminMenuBadges();

  let unreadThreads = 0;
  const { data: convs } = await supabase
    .from('conversations')
    .select('id')
    .eq('conversation_type', 'partner_support')
    .order('updated_at', { ascending: false })
    .limit(200);
  const convIds = (convs ?? []).map(c => c.id).filter(Boolean);
  if (convIds.length) {
    const { data: reads } = await supabase
      .from('conversation_reads')
      .select('conversation_id, last_read_at')
      .eq('reader_id', adminUserId)
      .in('conversation_id', convIds);
    const readMap = Object.fromEntries((reads ?? []).map(r => [r.conversation_id, r.last_read_at]));

    const { data: lastMsgs } = await supabase
      .from('messages')
      .select('conversation_id, created_at, sender_id')
      .in('conversation_id', convIds)
      .neq('sender_id', adminUserId)
      .order('created_at', { ascending: false })
      .limit(800);
    const lastByConv = {};
    for (const m of lastMsgs ?? []) {
      const cid = m?.conversation_id;
      if (!cid || lastByConv[cid]) continue;
      lastByConv[cid] = m.created_at;
    }
    unreadThreads = convIds.filter(cid => {
      const lastOtherAt = lastByConv[cid];
      if (!lastOtherAt) return false;
      const lr = readMap[cid];
      if (!lr) return true;
      return new Date(lastOtherAt) > new Date(lr);
    }).length;
  }

  let partnerRolesPending = 0;
  const { count: rolesPendingCount, error: rolesPendingErr } = await supabase
    .from('partner_roles')
    .select('id', { count: 'exact', head: true })
    .eq('role_status', 'pending_approval');
  if (!rolesPendingErr) partnerRolesPending = Math.max(0, Number(rolesPendingCount) || 0);

  let redesColaPendientes = 0;
  const { count: redesColaCount, error: redesColaErr } = await supabase
    .from('social_publish_queue')
    .select('id', { count: 'exact', head: true })
    .in('status', ['pending', 'processing', 'failed']);
  if (!redesColaErr) redesColaPendientes = Math.max(0, Number(redesColaCount) || 0);

  const badges = {
    partners: seg.partners,
    partner_registro: seg.partner_registro,
    partner_roles_pending: partnerRolesPending,
    noticias: seg.noticias,
    admin_mensajes: unreadThreads,
    publicidad: seg.publicidad,
    listings: seg.listings,
    valoraciones: seg.valoraciones,
    reportes_usuario: seg.reportes_usuario,
    reportes_listing: seg.reportes_listing,
    reportes_mensaje: seg.reportes_mensaje,
    reportes_error: seg.reportes_error,
    reportes: seg.reportes,
    comunidad_pendientes: seg.comunidad_pendientes,
    comunidad_eventos_n8n: seg.comunidad_eventos_n8n,
    comunidad_portada: seg.comunidad_portada,
    comunidad: seg.comunidad,
    eventos: seg.eventos,
    emails: seg.emails,
    redes_cola: redesColaPendientes,
    dashboard: 0,
  };

  badges.dashboard = computeAdminMenuDashboardTotal(badges);

  return badges;
}

export function emptyAdminMenuBadges() {
  return {
    partners: 0,
    partner_registro: 0,
    partner_roles_pending: 0,
    noticias: 0,
    admin_mensajes: 0,
    publicidad: 0,
    listings: 0,
    valoraciones: 0,
    reportes_usuario: 0,
    reportes_listing: 0,
    reportes_mensaje: 0,
    reportes_error: 0,
    reportes: 0,
    comunidad_pendientes: 0,
    comunidad_eventos_n8n: 0,
    comunidad_portada: 0,
    comunidad: 0,
    eventos: 0,
    emails: 0,
    redes_cola: 0,
    dashboard: 0,
  };
}

/** Punto rojo de aviso (pestañas y menú admin). */
export function AdminAlertDot({ title, size = 8 }) {
  return (
    <span
      title={title || 'Hay avisos pendientes'}
      aria-hidden
      style={{
        ...ADMIN_ALERT_DOT_STYLE,
        width: size,
        height: size,
      }}
    />
  );
}

/** Pastilla numérica junto al punto rojo. */
export function AdminAlertCount({ count }) {
  const n = Math.max(0, Number(count) || 0);
  if (n < 1) return null;
  return (
    <span
      style={{
        minWidth: 18,
        padding: '2px 6px',
        borderRadius: 999,
        background: 'rgba(226,75,74,0.95)',
        color: '#0a0a0f',
        fontSize: 10,
        fontWeight: 900,
        lineHeight: 1.2,
        textAlign: 'center',
        flexShrink: 0,
      }}
      title={`${n} pendiente${n === 1 ? '' : 's'}`}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

/** Contadores para pestañas del panel Comunidad (admin). */
export function adminComunidadTabBadges(badges) {
  return {
    pendientes: adminMenuBadgeCount(badges, 'comunidad_pendientes'),
    eventos_n8n: adminMenuBadgeCount(badges, 'comunidad_eventos_n8n'),
    portada: adminMenuBadgeCount(badges, 'comunidad_portada'),
    total: adminMenuBadgeCount(badges, 'comunidad'),
  };
}

/** Contadores para pestañas del panel Reportes (admin). */
export function adminReportesTabBadges(badges) {
  return {
    usuarios: adminMenuBadgeCount(badges, 'reportes_usuario'),
    anuncios: adminMenuBadgeCount(badges, 'reportes_listing'),
    mensajes: adminMenuBadgeCount(badges, 'reportes_mensaje'),
    errores: adminMenuBadgeCount(badges, 'reportes_error'),
    total: adminMenuBadgeCount(badges, 'reportes'),
  };
}

export function adminMenuBadgeCount(badges, itemId) {
  return Math.max(0, Number(badges?.[itemId]) || 0);
}

/** Ítems del menú lateral con badge (no pestañas internas ni claves agregadas). */
const ADMIN_SIDEBAR_BADGE_MENU_IDS = [
  'partners',
  'admin_mensajes',
  'partner_registro',
  'valoraciones',
  'reportes',
  'noticias',
  'comunidad',
  'listings',
  'eventos',
  'publicidad',
  'emails',
  'redes_cola',
];

/** Misma lógica que el badge de cada fila del menú admin. */
export function adminMenuItemBadgeCount(badges, itemId) {
  if (itemId === 'partners') {
    return (
      adminMenuBadgeCount(badges, 'partners') + adminMenuBadgeCount(badges, 'partner_roles_pending')
    );
  }
  if (itemId === 'dashboard') {
    return computeAdminMenuDashboardTotal(badges);
  }
  return adminMenuBadgeCount(badges, itemId);
}

/** Total global sin duplicar agregados (p. ej. comunidad + comunidad_eventos_n8n). */
export function computeAdminMenuDashboardTotal(badges) {
  if (!badges) return 0;
  return ADMIN_SIDEBAR_BADGE_MENU_IDS.reduce((sum, id) => sum + adminMenuItemBadgeCount(badges, id), 0);
}

export function adminMenuBadgeTotal(badges) {
  return computeAdminMenuDashboardTotal(badges);
}

export function adminMenuGroupBadgeSum(badges, items) {
  if (!items?.length) return 0;
  return items.reduce((acc, item) => acc + adminMenuItemBadgeCount(badges, item.id), 0);
}
